<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row>
        <el-col :span="12">
          <h1 class="pb-15">
            <v-svg class="voxo-blue" sprite="hash" />
            Feature Codes
          </h1>
        </el-col>

        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ total }}
          </el-tag>
        </el-col>
      </el-row>
      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable> </el-input>
        </div>
        <div class="box right">
          <el-button @click="openCreateModal">Add New</el-button>
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{enabled: true, gt: 50, oSize: 0}"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <create-form
      :codeInUse="codeInUse"
      :createVisible="createVisible"
      :createProcessing="createProcessing"
      :clearFormFields="clearFormFields"
      :draggableDestTypes="draggableDestTypes"
      @checkFeatureCode="checkFeatureCode"
      @createOperation="createOperation"
      @cancelOperation="cancelOperation"
    />

    <edit-form
      :codeInUse="codeInUse"
      :editVisible="editVisible"
      :editProcessing="editProcessing"
      :clearFormFields="clearFormFields"
      :draggableDestTypes="draggableDestTypes"
      :selectedFeature="selectedFeature"
      @checkFeatureCode="checkFeatureCode"
      @editOperation="editOperation"
      @cancelOperation="cancelOperation"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { debounce } from "lodash";
import {globalEventBus} from "../../../globalEventBus";

import CreateForm from "./components/createForm";
import EditForm from "./components/editForm";
import feathersClient from "../../../feathers/feathers-client";

export default {
  name: "GlobalFeaturesTable",
  data() {
    return {
      featuresChannel: null,
      tableData: [],
      allFeatures: [],
      tableLoading: false,
      deleteProcessing: false,
      search: "",
      featureCode: "",
      visibleActionsId: "",
      codeInUse: false,
      editVisible: false,
      editProcessing: false,
      createVisible: false,
      createProcessing: false,
      clearFormFields: false,
      selectedFeature: null,
      total: 0,
      draggableDestTypes: [
        "SPECIAL",
        "CUSTOM",
        "DISABLEUNCONDITIONALDID",
        "ENABLEUNCONDITIONALDID",
        "IVR",
        "VOICEMAIL",
        "LOGINQUEUE",
        "LOGOUTQUEUE",
        "PLAYBACK",
        "RERECORD",
        "HUNTLIST",
        "EXT",
        "QUEUE",
        "SMS",
        "MUSICONHOLD",
        "SETUNCONDITIONALDID",
        "CONDITION",
        "TOGGLEPAUSEQUEUE",
        "TOGGLEUNCONDITIONALDID"
      ],
      tableColumns: [
        {
          field: 'sprite',
          title: '',
          width: 50,
          slots: {
            default: ({row}) => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-blue" sprite="hash"/>
                </div>
              ]
            }
          }
        },
        {
          field: 'code',
          title: 'Code',
          minWidth: 100,
          sortable: true
        },
        {
          field: 'comment',
          title: 'Description',
          minWidth: 250,
          showOverflow: true
        },
        {
          field: 'actions',
          title: '',
          minWidth: '150',
          showOverflow: false,
          slots: {
            default: ({row}) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={'edit-'+ row.id}
                    icon="el-icon-edit"
                    onClick={() => this.openEditModal(row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={'delete-'+row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteOperation(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>
              ]
            }
          }
        }
      ]
    };
  },

  components: {
    CreateForm,
    EditForm
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    }
  },

  watch: {
    user() {
      this.findFeatures();
      if(!this.featuresChannel){
        this.joinSocketChannel();
      }
    },

    // ⭐ Checking for existing feature code ⭐ //
    featureCode: debounce(function() {
      const checkNumberService = feathersClient.service("check-number");
      checkNumberService
        .find({
          query: { tenantId: 0, number: this.featureCode }
        })
        .then(res => {
          this.codeInUse = res.inUse;
        });
    }, 500),

    search: debounce(function () {
      this.searchFeatures();
    }, 500)
  },

  methods: {
    ...mapActions("features", {
      createFeatures: "create",
      patchFeatures: "patch",
      deleteFeatures: "remove"
    }),

    searchFeatures() {
      if(this.search.length){
        this.tableData = this.tableData.filter(
          data =>
            data.code.toLowerCase().includes(this.search.toLowerCase()) ||
            data.comment.toLowerCase().includes(this.search.toLowerCase())
        );
      } else{
        this.tableData = [...this.allFeatures];
      }
      this.total = this.tableData.length;
    },

    checkFeatureCode(code) {
      if (code) {
        this.featureCode = code;
      }
    },

    createOperation(createBody) {
      if (createBody.destinations.length) {
        createBody.destinations = createBody.destinations.map(
          ({ id, type, order }) => ({ id, type, order })
        );
      }

      this.$confirm("Process global features create?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.createProcessing = true;
          this.codeInUse = false;

          this.createFeatures(createBody)
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Created successfully!",
                type: "success"
              });
              this.createProcessing = false;
              this.cancelOperation("create");
            })
            .catch(error => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }
              this.createProcessing = false;
              this.clearFormFields = false;
            });
        })
        .catch(() => true);
    },

    editOperation(editBody) {
      if (editBody.destinations.length) {
        editBody.destinations = editBody.destinations.map(
          ({ id, type, order }) => ({ id, type, order })
        );
      }

      this.$confirm("Process global features edit?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.editProcessing = true;
          this.codeInUse = false;

          this.patchFeatures([this.selectedFeature.id, editBody])
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Edited successfully!",
                type: "success"
              });
              this.editProcessing = false;

              this.clearFormFields = true;
              this.cancelOperation("edit");
            })
            .catch(error => {
              this.clearFormFields = false;

              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }

              this.editProcessing = false;
            });
        })
        .catch(() => true);
    },

    deleteOperation(id) {
      this.$confirm("Process global feature code delete?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.deleteProcessing = true;

          this.deleteFeatures(id)
            .then(() => {
              this.deleteProcessing = false;
              this.$notify({
                title: "Success",
                message: "Deleted successfully!",
                type: "success"
              });
            })
            .catch(error => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error"
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error"
                });
              }

              this.deleteProcessing = false;
            });
        })
        .catch(() => true);
    },

    cancelOperation(type) {
      if (type === "create") {
        this.createVisible = false;
      } else if (type === "edit") {
        this.editVisible = false;
      }
      this.clearFormFields = true;
      this.codeInUse = false;
    },

    //General Form and Table Operations
    cellClicked(data){
      if(data.column.property !== 'actions'){
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data){
      this.visibleActionsId = data.row.id;
    },

    rowLeave(){
      this.visibleActionsId = "";
    },

    openEditModal(id) {
      this.clearFormFields = false;
      feathersClient
        .service("features")
        .get(id)
        .then(res => {
          this.selectedFeature = res;
          this.editVisible = true;
        });
    },

    openCreateModal() {
      this.createVisible = true;
      this.clearFormFields = false;
    },

    findFeatures(){
      this.tableLoading = true;
      feathersClient.service('features').find({
        query: {
          tenantId: 0
        }
      }).then( res => {
        this.tableData = [...res];
        this.allFeatures = [...res];
        this.total = res.length;
        this.tableLoading = false;
      }).catch( () => {
        this.tableLoading = false;
      });
    },

    joinSocketChannel(){
      const channelService = feathersClient.service('channel-memberships');
      const data = {
        roomType: 'global-admins'
      }
      channelService.create(data).then(res => {this.featuresChannel = 'global-admins'});
    },

    removeListeners(){
      feathersClient.service('features').removeListener('patched');
      feathersClient.service('features').removeListener('created');
      feathersClient.service('features').removeListener('removed');
      globalEventBus.$off('appReconnected');
    }
  },
  created(){
    //event listeners
    //monitor socket connection and reconnect to channel
    globalEventBus.$on('appReconnected', () => {
      this.joinSocketChannel();
    });

    //record was edited
    //always refresh did destination options
    feathersClient.service('features').on('patched', (data) => {
      const updateIndex = this.tableData.findIndex(item => item.id === data.id);
      const updateAllIndex = this.allFeatures.findIndex(item => item.id === data.id);
      if(updateIndex >= 0){this.tableData.splice(updateIndex, 1, data);}
      if(updateAllIndex >= 0){ this.allFeatures.splice(updateAllIndex, 1, data);}
    });

    //record was created
    feathersClient.service('features').on('created', (data) => {
      this.tableData.push(data);
      this.allFeatures.push(data);
    });

    //record was deleted
    feathersClient.service('features').on('removed', (data) => {
      const indexToRemove = this.tableData.findIndex(item => item.id === data.id);
      const indexAllToRemove = this.allFeatures.findIndex(item => item.id === data.id);
      if(indexToRemove >= 0){this.tableData.splice(indexToRemove, 1);}
      if(indexAllToRemove >= 0){this.allFeatures.splice(indexAllToRemove, 1);}
    });
  },
  mounted() {
    if (this.user) {
      this.findFeatures();
      this.joinSocketChannel();
    }
  },
  beforeDestroy() {
    this.removeListeners();
    feathersClient.service('channel-memberships').remove(null, {query: {channelName: this.featuresChannel}});
  }
};
</script>
